/**
 * ScrapingHealthCard — "Saúde do Scraping": Apify balance + ScrapingBee
 * credits in one unified card. Used in /master dashboard (dark console).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Zap, Bug, RefreshCcw, Loader2, AlertTriangle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  ok:       { bar: "#10B981", label: "OK" },
  warning:  { bar: "#F59E0B", label: "Atenção" },
  critical: { bar: "#EF4444", label: "Crítico" },
};

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function ScrapingHealthCard() {
  const [apify, setApify] = useState(null);
  const [bee, setBee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true); setError("");
    Promise.all([
      axios.get(`${API}/apify/balance`),
      axios.get(`${API}/proxy/status`),
    ])
      .then(([a, b]) => { setApify(a.data); setBee(b.data); })
      .catch(() => setError("Falha ao carregar dados de scraping"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const st = STATUS_COLORS[apify?.status] || STATUS_COLORS.ok;
  const pct = Math.min(100, apify?.pct_used || 0);

  return (
    <div data-testid="scraping-health-card"
      className="rounded-xl p-5 border flex flex-col gap-4"
      style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.10)" }}>
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] uppercase tracking-wider font-bold text-white inline-flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#C9A24E]" /> Saúde do Scraping
        </h3>
        <button type="button" data-testid="shc-refresh" onClick={load}
          className="text-white/50 hover:text-white transition-colors" title="Atualizar">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {error && (
        <p className="text-[12px] text-red-300 inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </p>
      )}

      {/* Apify */}
      <div data-testid="shc-apify">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] uppercase tracking-wide font-bold text-white/55">
            Apify · {apify?.plan || "—"}
          </span>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ color: st.bar, background: `${st.bar}22` }}>
            {st.label}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[24px] font-extrabold text-white leading-none" data-testid="shc-apify-remaining">
            {apify ? fmtUsd(apify.remaining_usd) : "—"}
          </span>
          <span className="text-[11.5px] text-white/55">
            restantes de {apify ? fmtUsd(apify.limit_usd) : "—"}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 mt-2 overflow-hidden">
          <div className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: st.bar }} />
        </div>
        <div className="text-[10.5px] text-white/45 mt-1">
          {pct.toFixed(1)}% usado no ciclo
          {apify?.cycle_end && ` · renova ${new Date(apify.cycle_end).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}`}
        </div>
      </div>

      <div className="border-t border-white/10" />

      {/* ScrapingBee */}
      <div data-testid="shc-scrapingbee">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] uppercase tracking-wide font-bold text-white/55 inline-flex items-center gap-1.5">
            <Bug className="w-3.5 h-3.5 text-amber-400" /> ScrapingBee (proxy)
          </span>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={bee?.configured
              ? { color: "#10B981", background: "#10B98122" }
              : { color: "#EF4444", background: "#EF444422" }}>
            {bee?.configured ? "ATIVO" : "SEM KEY"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <div>
            <div className="text-[20px] font-extrabold text-white leading-none" data-testid="shc-bee-today">
              {bee?.today?.credits ?? 0}
            </div>
            <div className="text-[10.5px] text-white/45 mt-1">créditos hoje ({bee?.today?.requests ?? 0} reqs)</div>
          </div>
          <div>
            <div className="text-[20px] font-extrabold text-white leading-none" data-testid="shc-bee-30d">
              {bee?.last_30_days?.credits ?? 0}
            </div>
            <div className="text-[10.5px] text-white/45 mt-1">créditos 30 dias ({bee?.last_30_days?.requests ?? 0} reqs)</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[10.5px] pt-1">
        <a href="https://console.apify.com/billing" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-white/45 hover:text-white transition-colors">
          <ExternalLink className="w-3 h-3" /> Apify Console
        </a>
        <a href="https://app.scrapingbee.com/dashboard" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-white/45 hover:text-white transition-colors">
          <ExternalLink className="w-3 h-3" /> ScrapingBee Dashboard
        </a>
      </div>
    </div>
  );
}
